import type { BaseFields } from "@/resources/base";
import type {
  Package,
  PackageCampaignData,
  PackageWithId,
} from "@/resources/package";
import type { Filter } from "@/utils/apiFilter";
import type { Response } from "./apiClient";
import { apiClient } from "./apiClient";

class PackageService {
  async list(filter: Filter = {}): Promise<PackageWithId[]> {
    try {
      const response = await apiClient.get<PackageWithId[]>(
        "/packages",
        filter,
      );
      if (response.status === "OK" && response.result) {
        return response.result;
      }
      return [];
    } catch (error) {
      console.error("Failed to list packages:", error);
      return [];
    }
  }

  async create(
    payload: Omit<Package, keyof BaseFields>,
  ): Promise<Response<PackageWithId>> {
    try {
      const response = await apiClient.post<PackageWithId>(
        "/packages",
        payload,
      );
      if (response.status === "OK" && response.result) {
        return response;
      }
      return response;
    } catch (error) {
      console.error("Failed to create package:", error);
      return { status: "ERROR", errorMessage: "An unexpected error occurred" };
    }
  }

  async getByIdOrCode(packageIdOrCode: string): Promise<PackageWithId | null> {
    try {
      const response = await apiClient.get<PackageWithId>(
        `/packages/${packageIdOrCode}`,
      );
      if (response.status === "OK" && response.result) {
        return response.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to get package by ID or code:", error);
      return null;
    }
  }

  async updateByIdOrCode(
    packageIdOrCode: string,
    payload: Partial<Package>,
  ): Promise<boolean> {
    try {
      const response = await apiClient.put(
        `/packages/${packageIdOrCode}`,
        payload,
      );
      return response.status === "OK";
    } catch (error) {
      console.error("Failed to update package:", error);
      return false;
    }
  }

  async deleteByIdOrCode(packageIdOrCode: string): Promise<boolean> {
    try {
      const response = await apiClient.delete(`/packages/${packageIdOrCode}`);
      return response.status === "OK";
    } catch (error) {
      console.error("Failed to delete package:", error);
      return false;
    }
  }

  async getPackageCampaignData(
    packageId: string,
  ): Promise<PackageCampaignData | null> {
    try {
      const response = await apiClient.get<PackageCampaignData>(
        `/packages/${packageId}/package-results`,
      );
      if (response.status === "OK" && response.result) {
        return response.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to get package results:", error);
      return null;
    }
  }

  async getClassCode(packageId: string): Promise<string | null> {
    try {
      const response = await apiClient.post<{ classCode: string }>(
        `/packages/${packageId}/class-code`,
      );
      if (response.status === "OK" && response.result) {
        return response.result.classCode;
      }
      return null;
    } catch (error) {
      console.error("Failed to get class code:", error);
      return null;
    }
  }
}

export const packageService = new PackageService();
